import { destroy } from 'redux-form/immutable';
import {
    take, put, select, call
} from 'redux-saga/effects';
import {
    placeOrder, ORDER_SUCCESS, ORDER_FAILED, PAYMENT_FAILED
} from 'capi/redux';

import { resetIovation } from '../iovation/iovationModule';
import { itemReset } from '../item/itemModule';
import {
    getPromoItems,
    getPaidDigitalItems,
    getShippingGroups,
    getSubTotal,
    getGrandTotalCost,
    getShippingTotal,
    getActiveItems,
    getHasUYOPFees,
    getUYOPQuantity,
    getUYOPFeesTotal,
    getActivationFeesTotal,
    getHasActivationFees,
    getActivationFeesCardsCount
} from '../item/itemSelectors';
import { resetPromo, clearRejectedPromos } from '../promo/promoModule';
import { resetLoyalty } from '../loyalty/loyaltyModule';
import { getStorefrontSessionKey } from '../session/sessionSelectors';
import { parseOrderCompleted } from '../segment/segmentSelectors';
import { logError, logWarning } from '../utils/errorUtils';
import { getDiscountRewardAmount, getPromoState } from '../promo/promoSelectors';
import {
    getDigitalActivationFeeAmount,
    getPlasticActivationFeeAmount,
    getUYOPFeeAmount
} from '../catalogs/catalogSelectors';
import { orderComplete, showAppError } from '../routing/flowModule';
import { APP_ERROR_TYPES } from '../routing/flow';
import {
    resetCardholderAgreementAcceptance,
    resetGdprTermsAgreementAcceptance
} from '../consent/consentModule';

import { PAYMENT_FORM_NAME } from './paymentConstants';
import { orderSerializer, orderResponseSerializer, sanitizePaymentData } from './capiOrderV4ItemSerializer';
import { orderMetaData } from './paymentSelectors';
import {
    paymentMethodDataHandlers,
    paymentFailed,
    startSendOrder,
    paymentSuccess,
    paymentReset,
    PROCESS_PAYMENT
} from './paymentModule';


export const defaultFailureResponse = {
    status: 402,
    statusText: 'Payment Required',
    data: {
        errorCode: 'PAYMENT_REQUIRED'
    }
};

export const parseErrorResponse = response => ({
    httpCode: response.status,
    httpMessage: response.statusText,
    capiCode: response.data.errorCode
});

export function* calculateOrderFees() {
    const uyopFeeAmount = yield select(getUYOPFeeAmount);
    const plasticActivationFeeAmount = yield select(getPlasticActivationFeeAmount);
    const digitalActivationFeeAmount = yield select(getDigitalActivationFeeAmount);
    return {
        uyopFeeAmount: uyopFeeAmount ? uyopFeeAmount.toFixed(2) : '0.00',
        plasticActivationFeeAmount: plasticActivationFeeAmount ? plasticActivationFeeAmount.toFixed(2) : '0.00',
        digitalActivationFeeAmount: digitalActivationFeeAmount ? digitalActivationFeeAmount.toFixed(2) : '0.00'
    };
}


export function* orderingSaga(payment) {
    const metaData = yield select(orderMetaData);
    const digitalItems = yield select(getPaidDigitalItems);
    const shippingGroups = yield select(getShippingGroups);
    const fees = yield call(calculateOrderFees);
    const order = yield call(orderSerializer, payment, metaData, digitalItems, shippingGroups,
        fees);
    
    yield put(placeOrder(order.toJS()));
    
    const result = yield take([
        ORDER_SUCCESS, ORDER_FAILED, PAYMENT_FAILED
    ]);

    
    
    
    
    payment = sanitizePaymentData(payment);
    switch (result.type) {
        case ORDER_FAILED: {
            logWarning(result.error);
            const res = typeof result.error.response !== 'undefined'
                ? result.error.response : defaultFailureResponse;
            if (res.status === 504) {
                
                
                
                yield put(showAppError(APP_ERROR_TYPES.NO_RETRY));
            } else {
                const parsedResponse = parseErrorResponse(res);
                yield put(paymentFailed(parsedResponse));
            }
            break;
        }
        case PAYMENT_FAILED: {
            yield call(paymentErrorHandler, result.error, payment, payment.get('paymentMethod'));
            break;
        }
        case ORDER_SUCCESS: {
            
            
            
            try {
                const orderedItems = yield select(getActiveItems);
                
                
                
                yield put(paymentSuccess(result.data));

                const promoItems = yield select(getPromoItems);
                const orderData = yield call(orderResponseSerializer, result.data);
                const uyopFeesTotal = yield select(getUYOPFeesTotal);
                const activationFeesTotal = yield select(getActivationFeesTotal);
                yield put(clearRejectedPromos());
                const promoState = yield select(getPromoState);
                const orderDetails = {
                    orderData,
                    items: orderedItems.toJS(),
                    promoItems: promoItems.toJS(),
                    subtotal: parseFloat((yield select(getSubTotal)).toFixed(2)),
                    grandTotal: parseFloat((yield select(getGrandTotalCost)).toFixed(2)),
                    shipping: yield select(getShippingTotal),
                    sessionID: yield select(getStorefrontSessionKey),
                    paymentData: payment.set('purchaseDate', new Date()).toJS(),
                    totalDiscountRewardAmount: yield select(getDiscountRewardAmount),
                    hasUYOPFees: yield select(getHasUYOPFees),
                    uyoPlasticCount: yield select(getUYOPQuantity),
                    uyoPlasticFeesTotal: parseFloat(uyopFeesTotal.toFixed(2)),
                    hasActivationFees: yield select(getHasActivationFees),
                    activationFeesTotal: parseFloat(activationFeesTotal.toFixed(2)),
                    activationFeesCardsCount: yield select(getActivationFeesCardsCount),
                    promo: promoState.toJS()
                };
                const segmentOrderCompletedData = yield select(
                    parseOrderCompleted,
                    orderData.get('orderNumber'),
                    metaData.get('currencyCode')
                );

                yield put(orderComplete(orderDetails, segmentOrderCompletedData));
            } catch (e) {
                
                yield put(showAppError(APP_ERROR_TYPES.NO_RETRY));
                logError(`Order success handling failed: "${e}", bailing and showing the user a 500`);
            } finally {
                
                
                yield put(itemReset());
                yield put(destroy(PAYMENT_FORM_NAME));
                yield put(paymentReset());
                yield put(resetIovation());
                yield put(resetPromo());
                yield put(resetLoyalty());
                yield put(resetCardholderAgreementAcceptance());
                yield put(resetGdprTermsAgreementAcceptance());
            }
            break;
        }
        default:
            break;
    }
}


export function* paymentErrorHandler(error, payment, paymentMethod) {
    logWarning(`${paymentMethod} auth failed: ${error}`, payment);
    const parsedResponse = parseErrorResponse(defaultFailureResponse);
    yield put(paymentFailed(parsedResponse));
}


export function* placeOrderSaga() {
    
    while (true) {
        
        
        
        
        let { paymentMethod, paymentData } = yield take(PROCESS_PAYMENT);

        
        yield put(startSendOrder());

        try {
            const paymentMethodDataHandler = paymentMethodDataHandlers.get(paymentMethod);
            let payment = yield call(paymentMethodDataHandler, paymentData);

            
            if (payment.get('approved')) {
                yield call(orderingSaga, payment);
            } else {
                const paymentResponse = payment.get('response');
                payment = sanitizePaymentData(payment);
                yield call(paymentErrorHandler, paymentResponse, payment, paymentMethod);
            }
        } catch (e) {
            
            
            
            
            paymentData = sanitizePaymentData(paymentData);
            yield call(paymentErrorHandler, e, paymentData, paymentMethod);
        }
    }
}

export default placeOrderSaga;
